import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormik, type FormikErrors } from 'formik';
import { Grid } from '@mui/material';
import styled from 'styled-components';
import { object, array, string } from 'yup';
import { distinctiveScholarshipOptions } from './options';
import { PAGE_TEXT } from '../lang';
import {
  updateApplicationItem,
  type DistinctiveScholarships,
  type DistinctiveScholarship,
} from '../../../graphql';
import {
  Accordion,
  Alert,
  type AlertDetails,
  Button,
  DocumentBox,
  UploadBox,
  type FileData,
  FILE_MANAGE_ORDER,
  Select,
  TextField,
  NotificationText,
} from '../../../../../components';
import {
  type FormController,
  FormModificationMode,
} from '../../../../../utils';

type DistinctiveScholarshipProps = {
  distinctiveScholarships: DistinctiveScholarship[];
  applicationNo: string;
  entryYear: string;
  enableDelete: boolean;
  enableEdit: FormController;
  refetchApplication: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

const pageText = PAGE_TEXT.FELLOWSHIP.DISTINCTIVE_SCHOLARSHIP;
export const DistinctiveScholarshipForm: React.FC<
  DistinctiveScholarshipProps
> = (props: DistinctiveScholarshipProps) => {
  const {
    distinctiveScholarships,
    applicationNo,
    enableEdit,
    refetchApplication,
    handleLoading,
    enableDelete,
  } = props;

  const isDistinctiveApplicant =
    distinctiveScholarships.filter(
      scholarship =>
        scholarship.fundingSource !== '' &&
        scholarship.fundingSource !== undefined,
    ).length > 0;
  const { applicationId } = useParams();
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const updateDistinctiveScholarship = updateApplicationItem();
  const handleSubmitScholarship = async (
    values: DistinctiveScholarships,
  ): Promise<void> => {
    const distinctiveScholarships = values.distinctiveScholarships.filter(
      scholarship =>
        scholarship.fundingSource !== '' &&
        scholarship.fundingSource !== undefined,
    );
    await handleLoading(async () => {
      try {
        const variables = {
          id: applicationId,
          updateId: '', // can input any string here
          input: {
            distinctiveScholarships: {
              applicationId,
              distinctiveScholarships,
            },
          },
        };
        await updateDistinctiveScholarship({
          variables,
        });
        setAlertDetails({
          alertContent: 'Saved Distinctive Scholarship successfully. ',
          alertType: 'success',
        });
        refetchApplication();
      } catch (error) {
        setAlertDetails({
          alertContent:
            'Saved Distinctive Scholarship Failed.' + (error as Error).message,
          alertType: 'error',
        });
      }
    });
    setOpenAlert(true);
  };

  const formik = useFormik<DistinctiveScholarships>({
    initialValues: {
      distinctiveScholarships,
    },
    validationSchema: object({
      distinctiveScholarships: array().of(
        object({
          reason: string().when('fundingSource', ([fundingSource], schema) => {
            const required: boolean =
              fundingSource !== undefined && fundingSource !== '';
            return required
              ? schema.required('Required')
              : schema.notRequired();
          }),
        }),
      ),
    }),
    onSubmit: handleSubmitScholarship,
  });

  const handleChangeScholarship =
    (index: number) =>
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      const { value } = e.target;
      const { distinctiveScholarships } = formik.values;

      const clearScholarship = (): DistinctiveScholarship => ({
        fundingSource: '',
        reason: '',
        files: [],
      });

      const filteredValues =
        (distinctiveScholarshipOptions.filter(
          option => option.value !== value,
        )?.[0]?.value as string) ?? '';

      let updatedScholarships = [...distinctiveScholarships];

      // Handle the first scholarship (index 0)
      if (index === 0) {
        if (value === '') {
          updatedScholarships = [clearScholarship(), clearScholarship()];
        } else {
          updatedScholarships[0].fundingSource = value;
          if (value === distinctiveScholarships[1].fundingSource) {
            updatedScholarships[1].fundingSource = filteredValues;
          }
        }
      } else {
        if (value === '') {
          updatedScholarships[1] = clearScholarship();
        } else {
          updatedScholarships[1].fundingSource = value;
          if (value === distinctiveScholarships[0].fundingSource) {
            updatedScholarships[0].fundingSource = filteredValues;
          }
        }
      }

      await formik.setFieldValue(
        'distinctiveScholarships',
        updatedScholarships,
      );
    };

  const handleProofDocsChange =
    (index: number) =>
    async (order: number, file: FileData): Promise<boolean> => {
      const processingFileName = `${applicationNo}_distinctive_scholarship_proof_${index + 1}`;
      let newProof: FileData[] =
        formik.values.distinctiveScholarships[index].files;
      switch (order) {
        case FILE_MANAGE_ORDER.UPLOAD:
          newProof = [
            {
              id: file.id,
              link: file.link,
              name: processingFileName,
            },
          ];
          break;
        case FILE_MANAGE_ORDER.REMOVE:
          newProof = [];
          break;
        default:
      }
      await formik.setFieldValue(
        `distinctiveScholarships[${index}].files`,
        newProof,
      );
      return true;
    };

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <NotificationText note={PAGE_TEXT.FELLOWSHIP.DISTINCTIVE_TIPS} />
      <Accordion
        defaultExpanded
        title={pageText.TITLE}
        details={
          <Container>
            <form noValidate onSubmit={formik.handleSubmit}>
              <div
                className="mx-4 !text-black font-medium"
                dangerouslySetInnerHTML={{
                  __html: PAGE_TEXT.FELLOWSHIP.DISTINCTIVE_REMINDER,
                }}
              />

              <div
                className="mx-4 mb-8 !text-black font-medium"
                dangerouslySetInnerHTML={{
                  __html: PAGE_TEXT.FELLOWSHIP.DISTINCTIVE_HELPERTEXT,
                }}
              />

              {formik.values.distinctiveScholarships.map(
                (distinctiveScholarship, index) => {
                  const { fundingSource, reason, files } =
                    distinctiveScholarship;
                  const errors = formik.errors.distinctiveScholarships as
                    | Array<FormikErrors<DistinctiveScholarship>>
                    | undefined;

                  const disabledSecondPriority =
                    index === 1 &&
                    formik.values.distinctiveScholarships[0].fundingSource ===
                      '';
                  const enableModifyDistinctive =
                    enableEdit.formModificationMode ===
                      FormModificationMode.ENABLE ||
                    (enableEdit.formModificationMode ===
                      FormModificationMode.MODIFIED_ONLY &&
                      distinctiveScholarships[index].fundingSource !== '');

                  const displayReasonProof =
                    fundingSource !== '' && fundingSource !== undefined;

                  return (
                    <Grid
                      container
                      columnSpacing={4}
                      rowSpacing={2}
                      key={index}
                      marginBottom={'1rem'}
                      flexDirection="column"
                      className={'pl-4'}
                    >
                      <div className="font-medium">
                        {index
                          ? pageText.SECOND_PRIORITY
                          : pageText.FIRST_PRIORITY}
                      </div>
                      <Grid size={8}>
                        <Select
                          name={`distinctiveScholarships[${index}].fundingSource`}
                          value={fundingSource}
                          fullWidth
                          disabled={
                            disabledSecondPriority || !enableModifyDistinctive
                          }
                          InputLabelProps={{ shrink: true }}
                          options={distinctiveScholarshipOptions}
                          clearable={true}
                          onChange={handleChangeScholarship(index)}
                        />
                      </Grid>
                      {displayReasonProof && (
                        <>
                          <Grid size={12}>
                            <div className="mb-2">
                              {pageText.REASON_EXPLANATION}
                            </div>
                            <TextField
                              type="textarea"
                              name={`distinctiveScholarships[${index}].reason`}
                              value={reason}
                              minRows={3}
                              multiline
                              fullWidth
                              disabled={
                                !distinctiveScholarship.fundingSource ||
                                !enableModifyDistinctive
                              }
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                Boolean(
                                  formik.touched.distinctiveScholarships?.[
                                    index
                                  ]?.reason,
                                ) && Boolean(errors?.[index]?.reason)
                              }
                              helperText={
                                Boolean(
                                  formik.touched.distinctiveScholarships?.[
                                    index
                                  ]?.reason,
                                ) && errors?.[index]?.reason
                              }
                            />
                          </Grid>
                          <div className="flex flex-col mb-3 w-full">
                            <div className="mb-3">
                              {pageText.PROOF_DOCUMENT}
                            </div>
                            {enableModifyDistinctive && enableDelete ? (
                              <UploadBox
                                files={files}
                                maximumNum={1}
                                handleFilesChange={handleProofDocsChange(index)}
                              />
                            ) : (
                              <>
                                {files.map((file, index) => (
                                  <DocumentBox key={index} file={file} />
                                ))}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </Grid>
                  );
                },
              )}
              {/* Only formModificationMode equals to ENABLE or MODIFIED_ONLY(has filled distinctive before) can save upaf */}
              {(enableEdit.formModificationMode ===
                FormModificationMode.ENABLE ||
                (enableEdit.formModificationMode ===
                  FormModificationMode.MODIFIED_ONLY &&
                  isDistinctiveApplicant)) && (
                <Grid
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    margin: '2rem 0 1rem 0',
                  }}
                  size={{
                    md: 12,
                  }}
                >
                  <Button variant="contained" type="submit">
                    {PAGE_TEXT.SAVE}
                  </Button>
                </Grid>
              )}
            </form>
          </Container>
        }
      />
    </>
  );
};

const Container = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  form {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin-bottom: 2rem;
    width: 100%;
    padding: 0 1rem;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;
    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.1rem 1rem;
  }
`;
